import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { Control, FieldErrors, useWatch } from 'react-hook-form';
import { formatAmount } from 'src/utils/general';
import { useProfile } from 'src/utils/ProfileService';
import { ControlledTextarea } from 'src/components/ui/Textarea/Textarea';
import styles from './Customs.module.scss';
import { OverlaySectionHeadline } from '../../OverlaySectionHeadline/OverlaySectionHeadline';
import { ProductFormValues } from '../ProductFormTypes';
import CountrySelect from '../../../ui/CountrySelect/CountrySelect';

export interface CustomsProps {
  editModeActive: boolean;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  // weight: number | null;
}

const Customs: React.FC<CustomsProps> = ({ editModeActive, control, errors }) => {
  const { t } = useTranslation();
  const { profile, loading: profileLoading } = useProfile();
  const lang = profileLoading ? null : profile.lang;
  // The warehouse owns the net weight from its own confirmation on and writes
  // it back over FlowSync, so the API stops accepting it once a product leaves
  // draft (NON_DRAFT_EDITABLE_FIELDS). Same gate as the dimensions and the
  // gross weight in Dimensions.tsx. The other customs fields stay editable.
  const productState = useWatch({ control, name: 'productStatus' });
  const locked = !!productState && productState !== 'Draft';

  return (
    <div className={classNames(styles.root)}>
      <OverlaySectionHeadline as="h4">{t('general.customs')}</OverlaySectionHeadline>
      <div className="grid grid-cols-4 items-center gap-16 mb-40">
        <p className="typo-text-14--bold text-faded">{t('general.country_of_origin')}</p>
        <CountrySelect
          name="countryOfOrigin"
          className="col-span-full"
          isControlled
          control={control}
          editable={editModeActive}
          isInfoDisplay
        />
        <ControlledInput
          control={control}
          name="hsCode"
          error={!!errors.hsCode}
          errorMessage={errors.hsCode?.message}
          isInfoDisplay
          disabled={!editModeActive}
          className="col-span-2"
          type="string"
          placeholder={t('general.custom_tariff_number_placeholder')}
          label={t('general.custom_tariff_number')}
          maxLength={8}
        />
        <ControlledInput
          control={control}
          name="nettoWeight"
          error={editModeActive && !!errors.nettoWeight}
          errorMessage={editModeActive ? errors.nettoWeight?.message : undefined}
          isInfoDisplay
          disabled={locked || !editModeActive}
          className="col-span-2"
          type="number"
          placeholder=""
          label={`${t('general.netto_weight')} ${t('general.in_kg')}`}
          maxLength={20}
          formatDisplay={(v: any) => formatAmount(Number(v), lang)}
          numericLocale={lang}
        />
        <ControlledTextarea
          className="col-span-full"
          control={control}
          name="customsDescription"
          error={!!errors.customsDescription}
          errorMessage={errors.customsDescription?.message}
          disabled={!editModeActive}
          isInfoDisplay
          placeholder=""
          label={t('general.customs_description')}
          maxLength={2000}
        />
      </div>
    </div>
  );
};

export default Customs;
